import React from 'react';

import { useEpic } from 'contexts/EpicContext';

interface IExternalLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  title?: string;
  'data-test'?: string;
}

/**
 * A link out of the portal, opened wherever the host can actually open one.
 *
 * In an ordinary browser this is an anchor with `target="_blank"`. Inside Epic it is not: the
 * portal runs in an embedded webview that has no tabs, so `target="_blank"` either does nothing or
 * replaces the view the clinician is working in — which is why the tracking-number column already
 * asked Epic to open the default browser instead (`Epic.Common.OpenDefaultBrowser`). That rule was
 * written inline there and nowhere else, so the product-page links on Rates and pricing repeated
 * the mistake it exists to prevent.
 *
 * `noreferrer` alongside `noopener`: a page we send someone to has no business knowing which screen
 * of a clinical portal they came from.
 */
export const ExternalLink = ({
  href,
  children,
  className,
  title,
  'data-test': dataTest
}: IExternalLinkProps) => {
  const { publish, isReady } = useEpic();

  if (isReady) {
    return (
      <span
        role="link"
        tabIndex={0}
        className={`cursor-pointer text-primary ${className ?? ''}`}
        title={title}
        onClick={() => publish({ action: 'Epic.Common.OpenDefaultBrowser', args: href })}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            publish({ action: 'Epic.Common.OpenDefaultBrowser', args: href });
          }
        }}
        data-test={dataTest}
      >
        {children}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      title={title}
      data-test={dataTest}
    >
      {children}
    </a>
  );
};

export default ExternalLink;
